/**
 * browser-loop — W3C `traceparent` minting.
 *
 * Closed-loop §III.8.1 one-writer rule: the browser is the writer for
 * fetches it originates. We mint trace_id + span_id once per "user
 * turn" (typically one click → many fetches all share the trace), then
 * stamp `traceparent: 00-<trace>-<span>-01` on every outgoing fetch
 * via patches/fetch.ts.
 *
 * The backend's RequestContextMiddleware (Phase 0b shipped) parses
 * inbound `traceparent` and uses its trace_id/span_id as the root for
 * the request — and crucially does NOT mint a new trace_id when one
 * is inbound (Phase B span on_start processor, shipped Jun 10). The
 * cross-stack property holds when:
 *
 *   1. Browser mints trace_id BEFORE fetch starts
 *   2. Browser stamps traceparent on fetch
 *   3. Backend extracts, joins, propagates through Kafka envelope
 *   4. Backend sends response back with x-request-id + traceparent
 *      response headers (already shipped)
 *   5. Browser logs fetch.complete carrying same trace_id
 *
 * The forensics composite at GET /forensics/browser_session/{id} joins
 * browser_event rows to backend logs/spans by this trace_id — proven
 * live with the Layer 1 §V.2 smoke probe (Jun 23).
 */

import { uuidv7 } from "./identity";

interface TraceState {
  traceId: string; // 32 hex chars
  spanId: string; // 16 hex chars
  /** When this trace was minted — for auto-rotate after idle. */
  mintedAt: number;
}

let _currentTrace: TraceState | null = null;

/** Max age of a trace before we mint a new one even without explicit rotation. */
const TRACE_MAX_AGE_MS = 5 * 60 * 1000;

/** 32 hex chars from a UUID (strip dashes, take 32 = 128 bits). */
function trace32(): string {
  const u = uuidv7().replace(/-/g, "");
  // UUID is 32 hex chars exactly — perfect for trace_id.
  return u;
}

/** 16 hex chars from a UUID (take first 16). */
function span16(): string {
  return uuidv7().replace(/-/g, "").slice(0, 16);
}

/**
 * Mint a fresh trace. Called by:
 *   - patches/interaction.ts on user.click
 *   - patches/nav.ts on navigation
 *   - the page load lifecycle event
 */
export function mintTrace(): TraceState {
  _currentTrace = {
    traceId: trace32(),
    spanId: span16(),
    mintedAt: Date.now(),
  };
  return _currentTrace;
}

/**
 * Return the current trace, minting a fresh one if absent or expired.
 * The fetch patch calls this on every outgoing request.
 */
export function getOrMintTrace(): TraceState {
  if (
    !_currentTrace ||
    Date.now() - _currentTrace.mintedAt > TRACE_MAX_AGE_MS
  ) {
    return mintTrace();
  }
  return _currentTrace;
}

/** Read-only peek. Returns null if no trace yet — caller decides whether to mint. */
export function peekTrace(): TraceState | null {
  return _currentTrace;
}

/**
 * W3C traceparent format: `00-<trace32>-<span16>-<flags2>`.
 * Flags `01` = sampled.
 */
export function formatTraceparent(t: TraceState): string {
  return `00-${t.traceId}-${t.spanId}-01`;
}
